import React, { useEffect, useState, useCallback } from "react";
import AppShell from "@/components/AppShell";
import CroSubNav from "@/components/cro/CroSubNav";
import KpiCard from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, FileUp, ClipboardList, Users, FlaskConical, Clock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const KIND_META = {
  study:        { label: "Study",         icon: FlaskConical,   tone: "sky" },
  enrolment:    { label: "Enrolment",     icon: Users,          tone: "mint" },
  safety_event: { label: "Safety event",  icon: ClipboardList,  tone: "peach" },
  bulk_upload:  { label: "Bulk upload",   icon: FileUp,         tone: "lavender" },
};

export default function CroInputs() {
  const [portfolio, setPortfolio] = useState(null);
  const [pending, setPending] = useState(null);
  const [openKind, setOpenKind] = useState(null);

  const loadPending = useCallback(async () => {
    try {
      const res = await api.get("/cro/pending?limit=10");
      setPending(res.data);
    } catch (err) {
      console.error("Failed to load CRO intake pending queue:", err);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [p] = await Promise.all([api.get("/cro/portfolio")]);
        setPortfolio(p.data);
      } catch (err) {
        console.error("Failed to load CRO portfolio KPIs:", err);
      }
    })();
    loadPending();
  }, [loadPending]);

  const onSubmitted = () => {
    setOpenKind(null);
    loadPending();
  };

  return (
    <AppShell
      pageTitle="CRO Inputs"
      pageDescription="Contribute studies, patients, safety events and site data to the shared clinical-research intelligence layer."
      actions={<Button data-testid="cro-input-new-study" onClick={() => setOpenKind("study")}><Sparkles size={14} className="mr-1" /> Add new study</Button>}
    >
      <CroSubNav />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="cro-inputs-kpis">
        <KpiCard label="Active studies" value={portfolio?.totals?.active_studies ?? "—"} icon={FlaskConical} tone="sky" testid="input-kpi-studies" />
        <KpiCard label="Patients enrolled" value={portfolio?.totals?.patients_enrolled?.toLocaleString?.("en-IN") ?? "—"} icon={Users} tone="mint" testid="input-kpi-patients" />
        <KpiCard label="Investigator PIs" value={10} icon={ClipboardList} tone="lavender" testid="input-kpi-pi" />
        <KpiCard label="Pending records" value={pending?.totals?.pending ?? 0} icon={Clock} tone="peach" testid="input-kpi-pending" />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <IntakeCard testid="intake-study"     title="New study intake"    description="Register a Phase I-IV trial with sponsor, CRO, target enrolment and endpoints." icon={FlaskConical}  tone="sky"      onClick={() => setOpenKind("study")} />
        <IntakeCard testid="intake-enrolment" title="Log enrolment batch" description="Add newly randomised patients per site, with screening → randomisation deltas." icon={Users}          tone="mint"     onClick={() => setOpenKind("enrolment")} />
        <IntakeCard testid="intake-safety"    title="Report safety event" description="Submit an AE / SAE / SUSAR with CDSCO expedited-reporting cadence tracking."   icon={ClipboardList}  tone="peach"    onClick={() => setOpenKind("safety_event")} />
        <IntakeCard testid="intake-upload"    title="Bulk upload (CSV / EDC)" description="Ingest data from your EDC export or a validated CSV template."             icon={FileUp}         tone="lavender" onClick={() => setOpenKind("bulk_upload")} />
      </div>

      <PendingQueue data={pending} onRefresh={loadPending} />

      <StudyDialog       open={openKind === "study"}        onOpenChange={(v) => !v && setOpenKind(null)} onSubmitted={onSubmitted} />
      <EnrolmentDialog   open={openKind === "enrolment"}    onOpenChange={(v) => !v && setOpenKind(null)} onSubmitted={onSubmitted} />
      <SafetyDialog      open={openKind === "safety_event"} onOpenChange={(v) => !v && setOpenKind(null)} onSubmitted={onSubmitted} />
      <BulkUploadDialog  open={openKind === "bulk_upload"}  onOpenChange={(v) => !v && setOpenKind(null)} onSubmitted={onSubmitted} />
    </AppShell>
  );
}

function IntakeCard({ title, description, icon: Icon, tone, testid, onClick }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-xl border border-pastel-${tone}-border bg-white p-5 hover:bg-pastel-${tone}-bg transition-colors group`}
    >
      <div className={`w-10 h-10 rounded-md bg-pastel-${tone}-bg text-pastel-${tone}-fg inline-flex items-center justify-center mb-3 group-hover:scale-105 transition-transform`}>
        <Icon size={18} />
      </div>
      <div className="font-serif text-lg text-primary mb-1">{title}</div>
      <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
    </button>
  );
}

function PendingQueue({ data }) {
  if (!data) return null;
  return (
    <div className="mt-8 rounded-xl border border-border bg-white p-5" data-testid="pending-queue">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline text-muted-foreground">Pending review queue</div>
          <div className="font-serif text-lg text-primary">{data.totals.pending} record{data.totals.pending === 1 ? "" : "s"} awaiting reconciliation</div>
        </div>
        <div className="flex gap-1.5 text-[10px] uppercase tracking-wider">
          {Object.entries(data.totals).filter(([k]) => k !== "pending").map(([k, v]) => (
            <span key={k} className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground" data-testid={`pending-count-${k}`}>{KIND_META[k]?.label}: {v}</span>
          ))}
        </div>
      </div>
      {data.items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">No pending records — all intake is reconciled.</div>
      ) : (
        <ul className="divide-y divide-border" data-testid="pending-list">
          {data.items.map((it) => {
            const meta = KIND_META[it.kind] || { label: it.kind, icon: ClipboardList, tone: "sky" };
            const Icon = meta.icon;
            return (
              <li key={it.id} className="py-2.5 flex items-start gap-3" data-testid={`pending-item-${it.id}`}>
                <span className={`shrink-0 w-8 h-8 rounded-md bg-pastel-${meta.tone}-bg text-pastel-${meta.tone}-fg inline-flex items-center justify-center`}>
                  <Icon size={14} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-primary truncate">
                    {meta.label} · {intakeSummary(it)}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {new Date(it.submitted_at).toLocaleString("en-IN")} · {it.submitted_by.email} ({it.submitted_by.role})
                  </div>
                </div>
                <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-pastel-peach-bg text-pastel-peach-fg self-center">
                  {it.status}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function intakeSummary(it) {
  const p = it.payload || {};
  switch (it.kind) {
    case "study":        return `${p.name} · ${p.phase} · ${p.therapeutic_area}`;
    case "enrolment":    return `${p.study_id} · ${p.site_city} · scr ${p.screened} → rand ${p.randomised}`;
    case "safety_event": return `${p.study_id} · ${p.severity} · ${p.event_term}`;
    case "bulk_upload":  return `${p.study_id} · ${p.dataset} · ${p.row_count} rows${p.filename ? ` · ${p.filename}` : ""}`;
    default:             return it.id;
  }
}

// ---------------- Dialogs ----------------

function useSubmit(path, onDone) {
  const [busy, setBusy] = useState(false);
  const submit = async (body) => {
    setBusy(true);
    try {
      const res = await api.post(path, body);
      toast.success("Recorded", { description: `ID: ${res.data.id}` });
      onDone?.(res.data);
    } catch (e) {
      toast.error("Could not submit", { description: e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Please check the form." });
    } finally {
      setBusy(false);
    }
  };
  return { busy, submit };
}

function StudyDialog({ open, onOpenChange, onSubmitted }) {
  const [form, setForm] = useState({ name: "", phase: "Phase II", therapeutic_area: "", sponsor: "", cro: "", patients_target: 100, primary_endpoint: "" });
  const { busy, submit } = useSubmit("/cro/studies", onSubmitted);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dialog-study">
        <DialogHeader>
          <DialogTitle>New study intake</DialogTitle>
          <DialogDescription>Register a Phase I-IV clinical trial. Status will be <b>pending</b> until reviewed.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <Field label="Study name"><Input data-testid="study-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phase">
              <Select value={form.phase} onValueChange={(v) => setForm({ ...form, phase: v })}>
                <SelectTrigger data-testid="study-phase"><SelectValue /></SelectTrigger>
                <SelectContent>{["Phase I", "Phase II", "Phase III", "Phase IV"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Therapeutic area"><Input data-testid="study-ta" value={form.therapeutic_area} onChange={(e) => setForm({ ...form, therapeutic_area: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sponsor"><Input data-testid="study-sponsor" value={form.sponsor} onChange={(e) => setForm({ ...form, sponsor: e.target.value })} /></Field>
            <Field label="CRO (optional)"><Input data-testid="study-cro" value={form.cro} onChange={(e) => setForm({ ...form, cro: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Target enrolment"><Input type="number" data-testid="study-target" value={form.patients_target} onChange={(e) => setForm({ ...form, patients_target: Number(e.target.value) || 0 })} /></Field>
            <Field label="Primary endpoint (optional)"><Input data-testid="study-endpoint" value={form.primary_endpoint} onChange={(e) => setForm({ ...form, primary_endpoint: e.target.value })} /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={busy} data-testid="study-submit" onClick={() => submit({ ...form, cro: form.cro || undefined, primary_endpoint: form.primary_endpoint || undefined })}>{busy ? "Saving…" : "Submit for review"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EnrolmentDialog({ open, onOpenChange, onSubmitted }) {
  const [form, setForm] = useState({ study_id: "", site_city: "", screened: 0, randomised: 0, week_ending: "" });
  const { busy, submit } = useSubmit("/cro/enrolment", onSubmitted);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dialog-enrolment">
        <DialogHeader>
          <DialogTitle>Log enrolment batch</DialogTitle>
          <DialogDescription>Weekly randomisation deltas per site. Study ID must match a live trial.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Study ID"><Input data-testid="enrol-study-id" value={form.study_id} onChange={(e) => setForm({ ...form, study_id: e.target.value })} placeholder="e.g. cro_apx_101" /></Field>
            <Field label="Site city"><Input data-testid="enrol-city" value={form.site_city} onChange={(e) => setForm({ ...form, site_city: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Screened"><Input type="number" data-testid="enrol-screened" value={form.screened} onChange={(e) => setForm({ ...form, screened: Number(e.target.value) || 0 })} /></Field>
            <Field label="Randomised"><Input type="number" data-testid="enrol-randomised" value={form.randomised} onChange={(e) => setForm({ ...form, randomised: Number(e.target.value) || 0 })} /></Field>
            <Field label="Week ending"><Input type="date" data-testid="enrol-week" value={form.week_ending} onChange={(e) => setForm({ ...form, week_ending: e.target.value })} /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={busy} data-testid="enrol-submit" onClick={() => submit({ ...form, week_ending: form.week_ending || undefined })}>{busy ? "Saving…" : "Submit for review"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SafetyDialog({ open, onOpenChange, onSubmitted }) {
  const [form, setForm] = useState({ study_id: "", severity: "AE", event_term: "", onset_date: "", outcome: "", narrative: "" });
  const { busy, submit } = useSubmit("/cro/safety-events", onSubmitted);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dialog-safety">
        <DialogHeader>
          <DialogTitle>Report safety event</DialogTitle>
          <DialogDescription>AE / SAE / SUSAR events feed the CDSCO expedited-reporting cadence.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Study ID"><Input data-testid="safety-study-id" value={form.study_id} onChange={(e) => setForm({ ...form, study_id: e.target.value })} placeholder="e.g. cro_apx_101" /></Field>
            <Field label="Severity">
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger data-testid="safety-severity"><SelectValue /></SelectTrigger>
                <SelectContent>{["AE", "SAE", "SUSAR"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Event term"><Input data-testid="safety-term" value={form.event_term} onChange={(e) => setForm({ ...form, event_term: e.target.value })} placeholder="e.g. Neutropenia grade 3" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Onset date"><Input type="date" data-testid="safety-date" value={form.onset_date} onChange={(e) => setForm({ ...form, onset_date: e.target.value })} /></Field>
            <Field label="Outcome (optional)"><Input data-testid="safety-outcome" value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} placeholder="e.g. Resolved" /></Field>
          </div>
          <Field label="Narrative (optional)"><Textarea rows={3} data-testid="safety-narrative" value={form.narrative} onChange={(e) => setForm({ ...form, narrative: e.target.value })} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={busy} data-testid="safety-submit" onClick={() => submit({ ...form, outcome: form.outcome || undefined, narrative: form.narrative || undefined })}>{busy ? "Saving…" : "Submit for review"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkUploadDialog({ open, onOpenChange, onSubmitted }) {
  const [form, setForm] = useState({ study_id: "", dataset: "edc", row_count: 100, filename: "", notes: "" });
  const { busy, submit } = useSubmit("/cro/bulk-upload", onSubmitted);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dialog-bulk">
        <DialogHeader>
          <DialogTitle>Bulk upload manifest</DialogTitle>
          <DialogDescription>Registers a dataset drop for reconciliation. Attach the raw file via SFTP/portal after review.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Study ID"><Input data-testid="bulk-study-id" value={form.study_id} onChange={(e) => setForm({ ...form, study_id: e.target.value })} /></Field>
            <Field label="Dataset">
              <Select value={form.dataset} onValueChange={(v) => setForm({ ...form, dataset: v })}>
                <SelectTrigger data-testid="bulk-dataset"><SelectValue /></SelectTrigger>
                <SelectContent>{["edc", "labs", "imaging", "randomisation", "other"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Row count"><Input type="number" data-testid="bulk-rows" value={form.row_count} onChange={(e) => setForm({ ...form, row_count: Number(e.target.value) || 0 })} /></Field>
            <Field label="Filename (optional)"><Input data-testid="bulk-filename" value={form.filename} onChange={(e) => setForm({ ...form, filename: e.target.value })} /></Field>
          </div>
          <Field label="Notes (optional)"><Textarea rows={3} data-testid="bulk-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={busy} data-testid="bulk-submit" onClick={() => submit({ ...form, filename: form.filename || undefined, notes: form.notes || undefined })}>{busy ? "Saving…" : "Submit for review"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
